import { type CSSProperties } from "react";

import { type Chart, type TooltipModel } from "chart.js";
import moment from "moment";
import { renderToString } from "react-dom/server";

import { isAnnotationDataset } from "@features/custom-annotations";
import {
  type CurrencyCode,
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { formatDateRange } from "@shared/date-range-picker";

import {
  getChartDataManagerOptions,
  type ChartDataManagerPluginOptions,
  type CustomChartDataset,
} from "../charts/plugins/chart-data-manager-plugin";
import { checkShouldDrawIconsInChart } from "../charts/plugins/split-chart-data-view-plugin";
import { type MetricFilters } from "../hooks/use-chart-state";

import {
  COMPARE_METRIC_1_BENCHMARK_ID,
  COMPARE_METRIC_1_ID,
  COMPARE_METRIC_2_BENCHMARK_ID,
  COMPARE_METRIC_2_ID,
  isBarChartCheck,
  MAIN_METRIC_BENCHMARK_ID,
  MAIN_METRIC_ID,
} from "./chart";
import { getTimeGranularityDateFormat } from "./compare-dates-helpers";
import { type CompareQueryMetadata } from "./compare-query";

export const findOrCreateTooltip = (id: string) => {
  let tooltipEl = document.getElementById(id);

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = id;
    tooltipEl.innerHTML = "<div></div>";
    const styles: CSSProperties = {
      opacity: "1",
      zIndex: "3000",
      position: "absolute",
      left: "",
      right: "",
      minWidth: "200px",
      transition: "opacity 0.2s ease",
    };
    Object.assign(tooltipEl.style, styles as CSSStyleDeclaration);

    document.body.appendChild(tooltipEl);
  }
  return tooltipEl;
};

export const buildTooltipContainer = (stringifyHtml: string) => {
  const container = document.createElement("div");

  container.classList.add(
    "relative",
    "shadow",
    "rounded-md",
    "border",
    "py-2",
    "px-3",
    "bg-white",
  );
  const caretHtml = `
    <div
      class="h-3 w-3 bg-white absolute left-1/2 tooltip-caret"
      style="
        border-top: 1px solid #B5B9C0;
        border-left: 1px solid #B5B9C0;
        border-bottom-color: transparent;
        border-right-color: transparent;
        transform: rotate(225deg) translate(1px, -50%);
      ">
    </div>
  `;

  container.innerHTML = stringifyHtml + caretHtml;
  return container.outerHTML;
};

export const buildCompareNameAndIconHtml = ({
  queryMetadata,
  dataset,
  hasComparisons,
}: {
  queryMetadata: CompareQueryMetadata | null;
  dataset: CustomChartDataset;
  hasComparisons: boolean;
}) => {
  if (
    queryMetadata?.icon &&
    queryMetadata?.optionLabel &&
    hasComparisons &&
    dataset.compare?.queryKey !== "metricName"
  ) {
    return `
    <div class="text-interstellar text-xs pr-3 truncate">
      <span>
        ${renderToString(queryMetadata.icon)}
      </span>
      <span>
        ${queryMetadata.optionLabel}
      </span>
    </div>`;
  }
  return "";
};

export const buildNameAndIconHtml = (
  chart: Chart,
  dataset: CustomChartDataset,
) => {
  if (dataset.name) {
    const shouldDrawIcons = checkShouldDrawIconsInChart(chart);
    return `
      <div class="flex items-center pr-2 max-w-[20rem]">
        ${
          shouldDrawIcons
            ? `<span style="width:16px;" class="mr-1">
            ${
              dataset.getIcon?.({
                borderColor: dataset.baseBorderColor,
                size: "16",
              }) || ""
            }
          </span>`
            : ""
        }
        <span class="text-xs truncate">
          ${dataset.name}
        </span>
      </div>`;
  }

  return buildLegend(
    {
      borderColor: dataset.baseBorderColor,
      backgroundColor: dataset.baseBackgroundColor,
      borderDash: dataset.borderDash,
    },
    isBarChartCheck(chart),
  );
};

export const buildLegendImage = ({
  borderColor,
  backgroundColor,
  borderDash = [],
  isBarChart,
  width = 30,
  height = 12,
  borderWidth = 2,
  pointRadius = 0,
}: {
  borderColor: string;
  backgroundColor: string | CanvasGradient | CanvasPattern;
  borderDash: number[];
  isBarChart: boolean;
  width?: number;
  height?: number;
  borderWidth?: number;
  pointRadius?: number;
}) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    canvas.width = width;
    canvas.height = height;
    ctx.beginPath();
    ctx.fillStyle = "#fff";
    if (isBarChart) {
      ctx.strokeStyle = borderColor;
      ctx.roundRect(
        borderWidth / 2,
        borderWidth / 2,
        width - borderWidth / 2,
        height - borderWidth / 2,
        0,
      );
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.strokeStyle = borderColor;
      ctx.setLineDash(borderDash);
      ctx.lineWidth = borderWidth;
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
    }
    ctx.stroke();

    if (pointRadius && pointRadius > 0) {
      ctx.beginPath();
      ctx.fillStyle = backgroundColor;
      ctx.arc(width / 2, height / 2, pointRadius, 0, 2 * Math.PI, false);
      ctx.fill();
    }
  }

  return canvas;
};

export const buildLegend = (
  dataset: {
    borderColor: string;
    backgroundColor: string | CanvasGradient | CanvasPattern;
    borderDash: number[];
    pointRadius?: number;
  },
  isBarChart: boolean,
) => {
  return `<img class="pr-1" src="${buildLegendImage({
    ...dataset,
    isBarChart,
  }).toDataURL("image/png")}" />`;
};

export const getCurrentMetric = (
  dataset: CustomChartDataset,
  {
    selectedMetrics,
    chartConfig: { comparisons },
  }: ChartDataManagerPluginOptions,
) => {
  const comparisonMetricIds = [COMPARE_METRIC_1_ID, COMPARE_METRIC_2_ID];
  const metricIndex = comparisons.findIndex(
    (comparison, index) =>
      comparison?.queryKey === "metricName" &&
      dataset.realID === comparisonMetricIds[index],
  );

  return metricIndex > -1
    ? selectedMetrics[metricIndex + 1]
    : selectedMetrics[0];
};

export const findCompareQueryMetadata = (
  dataset: CustomChartDataset,
  {
    getCompareQueryMetadata,
    pageConfiguration,
    pageConfiguration: { compareDateRange, dateRange },
    chartConfig: { comparisons },
  }: ChartDataManagerPluginOptions,
) => {
  const [comparison1] = comparisons;
  let queryMetadata = null;

  if (dataset.compare?.queryKey) {
    // First comparison
    queryMetadata = getCompareQueryMetadata(dataset.compare);
    if (dataset.compare.queryKey === "compareDateRange") {
      const compareDateRangeDisplayText = compareDateRange
        ? formatDateRange(compareDateRange as DateInterval)
        : "";
      queryMetadata.optionLabel =
        compareDateRangeDisplayText || queryMetadata.optionLabel;
    }
  } else if (comparison1) {
    // Main query
    const { queryKey } = comparison1;
    queryMetadata = getCompareQueryMetadata({
      queryKey,
      queryValue: (pageConfiguration as any)[queryKey],
    });
    queryMetadata.optionLabel =
      queryKey === "compareDateRange"
        ? formatDateRange(dateRange as DateInterval)
        : queryMetadata.optionLabel;
  }
  return queryMetadata;
};

const formatWeekRange = (
  date: number | Date | undefined,
  dateRangeInterval: DateInterval | null,
) => {
  if (!date) {
    return "";
  }

  const weekStart = moment(date);
  const weekEnd = moment(date).endOf("week");

  const endDate =
    dateRangeInterval && weekEnd.isAfter(dateRangeInterval.endDate)
      ? moment(dateRangeInterval.endDate)
      : weekEnd;

  const dateFormat = "ddd, MMM D";

  return `${weekStart.format(dateFormat)} - ${endDate.format(dateFormat)}`;
};

export const formatTooltipDate = (
  date: number | Date | undefined,
  timeGranularity: ITimeGranularityV2,
  dateRangeInterval: DateInterval | null,
) => {
  if (!date) {
    return "";
  }

  if (timeGranularity === "weekly") {
    return formatWeekRange(date, dateRangeInterval);
  } else {
    const dateFormat = getTimeGranularityDateFormat(timeGranularity);
    return moment(date).format(dateFormat);
  }
};

const buildFiltersHtml = (
  dataset: CustomChartDataset,
  chartDataManagerOptions: ChartDataManagerPluginOptions,
) => {
  const MAX_FILTERS_TO_DISPLAY = 6;

  let filters: MetricFilters[] = [];
  const isMain = dataset.realID === MAIN_METRIC_ID;
  const isCompare1 = dataset.realID === COMPARE_METRIC_1_ID;
  const isCompare2 = dataset.realID === COMPARE_METRIC_2_ID;
  if (isMain) {
    filters = chartDataManagerOptions.chartConfig.mainMetric.filters;
  } else if (isCompare1) {
    filters = chartDataManagerOptions.chartConfig.comparisons[0]?.filters;
  } else if (isCompare2) {
    filters = chartDataManagerOptions.chartConfig.comparisons[1]?.filters;
  }
  filters ||= [];

  if (filters.length) {
    let currentFilterCount = 0;
    const totalFilterCount = filters.reduce(
      (acc, { values }) => acc + values.length,
      0,
    );

    const flatFilters = filters.map((e) => ({
      key: e.breakdown.key,
      values: e.values.map((v) => v.value),
    }));

    if (chartDataManagerOptions.pageConfiguration.filters) {
      chartDataManagerOptions.pageConfiguration.filters.forEach((filter) => {
        const found = flatFilters.find((e) => e.key == filter.key);

        if (found) {
          filter.values.forEach((v) => {
            if (!found.values.includes(v)) {
              found.values.push(v);
            }
          });
        } else {
          flatFilters.push({ key: filter.key, values: [...filter.values] });
        }
      });
    }

    return `
      <div class="border-t border-b border-gray-1 mb-2 -mx-3 py-2 px-3">
        <div class="text-xs font-semibold">
          FILTERS
        </div>
        ${flatFilters
          .map(({ key, values }) => {
            if (currentFilterCount < MAX_FILTERS_TO_DISPLAY) {
              return `
                <div class="my-2">
                  <div class="text-xxs font-medium text-gray+2 leading-relaxed">
                    ${key}
                  </div>
                  ${values
                    .map((value) => {
                      currentFilterCount += 1;
                      if (currentFilterCount <= MAX_FILTERS_TO_DISPLAY) {
                        return `<div class="text-xxs leading-relaxed text-interstellar+2">${value}</div>`;
                      }
                      return "";
                    })
                    .join("")}
                </div>
              `;
            }
            return "";
          })
          .join("")}
          ${
            totalFilterCount > MAX_FILTERS_TO_DISPLAY
              ? `
                <div class="text-gray+2 text-xxs">
                  More filters affecting this data point...
                </div>
              `
              : ""
          }
      </div>
    `;
  }

  return "";
};

export const customTooltip =
  (currencyCode: CurrencyCode) =>
  ({ tooltip, chart }: { tooltip: TooltipModel<any>; chart: Chart<any> }) => {
    const tooltipEl = findOrCreateTooltip("sales-v3-tooltip");

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    const [dataPoint] = tooltip.dataPoints;
    const { dataset, dataIndex, parsed } = dataPoint as {
      dataset: CustomChartDataset & { label: string };
      parsed: { x: number | Date; y: number };
      dataIndex: number;
    };

    const isBenchmark = [
      String(MAIN_METRIC_BENCHMARK_ID),
      String(COMPARE_METRIC_1_BENCHMARK_ID),
      String(COMPARE_METRIC_2_BENCHMARK_ID),
    ].includes(dataset.realID);

    if (tooltip.body) {
      if (isAnnotationDataset(dataset)) {
        tooltipEl.style.opacity = "0";
        return;
      }

      let rootDataset = dataset;

      if (dataset.realID === MAIN_METRIC_BENCHMARK_ID) {
        const foundDataset = chart.data.datasets.find(
          (e) => e.realID === MAIN_METRIC_ID,
        );

        if (foundDataset) {
          rootDataset = foundDataset;
        }
      }

      if (dataset.realID === COMPARE_METRIC_1_BENCHMARK_ID) {
        const foundDataset = chart.data.datasets.find(
          (e) => e.realID === COMPARE_METRIC_1_ID,
        );

        if (foundDataset) {
          rootDataset = foundDataset;
        }
      }

      if (dataset.realID === COMPARE_METRIC_2_BENCHMARK_ID) {
        const foundDataset = chart.data.datasets.find(
          (e) => e.realID === COMPARE_METRIC_2_ID,
        );

        if (foundDataset) {
          rootDataset = foundDataset;
        }
      }

      const chartDataManagerOptions = getChartDataManagerOptions(chart);
      const currentMetric = getCurrentMetric(
        rootDataset,
        chartDataManagerOptions,
      );

      const queryMetadata = findCompareQueryMetadata(
        rootDataset,
        chartDataManagerOptions,
      );

      const { timeGranularity, dateRangeInterval, compareDateRangeInterval } =
        chartDataManagerOptions.pageConfiguration;
      let tooltipDate = formatTooltipDate(
        parsed.x,
        timeGranularity,
        dateRangeInterval,
      );

      // This is to display the date for the comparison date range in the tooltip
      const [comparison1] = chartDataManagerOptions.chartConfig.comparisons;
      if (comparison1?.queryKey === "compareDateRange" && queryMetadata) {
        const { compareDateRangeLabels, labels } = chartDataManagerOptions;

        const dateFormat = getTimeGranularityDateFormat(timeGranularity);

        if (dataset.realID === MAIN_METRIC_ID) {
          queryMetadata.optionLabel =
            timeGranularity === "weekly"
              ? formatWeekRange(labels?.[dataIndex], dateRangeInterval)
              : moment(labels?.[dataIndex]).format(dateFormat);
        } else if (
          dataset.realID === COMPARE_METRIC_1_ID &&
          compareDateRangeLabels
        ) {
          const compareDate = compareDateRangeLabels?.[dataIndex];

          queryMetadata.optionLabel =
            timeGranularity === "weekly"
              ? formatWeekRange(compareDate, compareDateRangeInterval)
              : moment(compareDate).format(dateFormat);
          tooltipDate = formatTooltipDate(
            compareDate,
            timeGranularity,
            compareDateRangeInterval,
          );
        }
      }

      const filtersHtml = buildFiltersHtml(
        rootDataset,
        chartDataManagerOptions,
      );
      const legendAndNameHtml = buildNameAndIconHtml(chart, dataset);

      const compareNameAndIconHtml = buildCompareNameAndIconHtml({
        queryMetadata,
        dataset,
        hasComparisons:
          chartDataManagerOptions.chartConfig.comparisons.length > 0,
      });

      const innerHTML = `
      <div class="max-w-[30rem] " >
        <div class="font-bold text-lg">
          ${currentMetric?.name} ${isBenchmark ? "Benchmark" : ""}
        </div>

        <div class="text-gray+1 text-sm font-light mb-2">
          ${tooltipDate}
        </div>
        ${filtersHtml}
        <div class="flex items-center justify-between space-x-2 text-xs w-full">
          ${legendAndNameHtml}
          ${compareNameAndIconHtml}
          <div class="font-medium font-mono ml-2">${currentMetric.formatter(
            parsed.y,
            currencyCode,
            { precision: "exact" },
          )}</div>
        </div>
        <div class="text-xs text-gray+1 font-light mt-1">
        Click to add note
        </div>
      </div>
    `;

      tooltipEl.innerHTML = buildTooltipContainer(innerHTML);
    }

    const position = chart.canvas.getBoundingClientRect();
    const styles: CSSProperties = {
      opacity: "1",
      zIndex: "2999",
      position: "absolute",
      left: "",
      right: "",
      top: position.top + window.pageYOffset + tooltip.caretY - 14 + "px",
      pointerEvents: "none",
    };

    const tooltipCaretEl: any = tooltipEl.querySelector(".tooltip-caret");
    const tooltipLeftPosition =
      tooltip.caretX + position.left + window.pageXOffset;

    const isOutOfBoundsRight =
      tooltipLeftPosition + tooltipEl.offsetWidth / 2 > window.innerWidth;
    const isOutOfBoundsLeft =
      tooltipLeftPosition - tooltipEl.offsetWidth / 2 < 0;
    if (isOutOfBoundsLeft && tooltipCaretEl) {
      tooltipCaretEl.classList.remove("left-1/2");
      tooltipCaretEl.style.left = tooltipLeftPosition + "px";
      styles.left = "0px";
      styles.transform = "translate(0%, -100%)";
    } else if (isOutOfBoundsRight && tooltipCaretEl) {
      tooltipCaretEl.classList.remove("left-1/2");
      const CARET_WIDTH = 12; // w-3 class on caret
      tooltipCaretEl.style.right = `${
        window.innerWidth - tooltipLeftPosition - CARET_WIDTH
      }px`;
      styles.right = "0px";
      styles.transform = "translate(0%, -100%)";
    } else {
      styles.left = tooltipLeftPosition + "px";
      styles.transform = "translate(-50%, -100%)";
    }

    Object.assign(tooltipEl.style, styles as CSSStyleDeclaration);

    // Hides the chart summary tooltip if it's visible
    const chartTooltip = document.getElementById("sales-v3-summary-tooltip");
    if (chartTooltip && chartTooltip.style.opacity === "1") {
      tooltipEl.style.opacity = "0";
    }
  };
